import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClientQuizError, getPublicQuiz } from '../../store/clientQuiz/api';
import type { PublicQuiz, QuizType } from '../../store/clientQuiz/api';
import { L, useTx } from './quizLang';
import type { Txt } from './quizLang';

/**
 * What both public quiz pages (保障小測驗, 職涯測驗) do with a `?t=` link, in one
 * place: resolve it, say why it cannot be played, and send answers with a retry.
 *
 * A link that cannot be read for a reason that may pass — no network, a 5xx —
 * is `offline`, with 重試, not 連結已失效: telling a client with a weak signal
 * that the adviser's link is dead loses the client. A 429 is `busy`, also with
 * 重試: the client's network is fine, so it must not tell them to check it.
 */

export type TerminalLinkState = 'invalid' | 'expired' | 'done' | 'offline' | 'busy' | 'mismatch';
export type LinkState = 'loading' | 'ready' | 'guest' | TerminalLinkState;

export const LINK_STATE_MESSAGES: Record<TerminalLinkState, Txt> = {
  // Each dead end names the way out: a link is answered once (§14 #2), and a
  // retake is a new link from the adviser.
  invalid: L(
    '連結已失效，請向你的顧問索取新連結',
    '链接已失效，请向你的顾问索取新链接',
    'This link no longer works. Please ask your adviser for a new one.'
  ),
  expired: L(
    '連結已過期，請向你的顧問索取新連結',
    '链接已过期，请向你的顾问索取新链接',
    'This link has expired. Please ask your adviser for a new one.'
  ),
  done: L(
    '你已完成這份測驗，謝謝！如需重做，請向你的顧問索取新連結',
    '你已完成这份测验，谢谢！如需重做，请向你的顾问索取新链接',
    "You've already finished this quiz, thank you! To take it again, please ask your adviser for a new link."
  ),
  offline: L(
    '暫時未能載入測驗，請檢查網絡後再試',
    '暂时未能加载测验，请检查网络后再试',
    "The quiz couldn't load right now. Please check your connection and try again."
  ),
  busy: L(
    '目前使用人數較多，請稍等一會再試',
    '目前使用人数较多，请稍等一会再试',
    'Lots of people are using this right now. Please wait a moment and try again.'
  ),
  // A 保障小測驗 token opened on the 職涯測驗 page (or the reverse): answering it
  // here would store this quiz's answers as the other one's result.
  mismatch: L(
    '這條連結屬於另一份測驗，請直接打開顧問發送的原本連結',
    '这条链接属于另一份测验，请直接打开顾问发送的原本链接',
    'This link belongs to a different quiz. Please open the original link your adviser sent.'
  )
};

const RETRY = L('重試', '重试', 'Try again');

export const isTerminalLinkState = (state: LinkState): state is TerminalLinkState =>
  state === 'invalid' ||
  state === 'expired' ||
  state === 'done' ||
  state === 'offline' ||
  state === 'busy' ||
  state === 'mismatch';

/** 400 / 404: the token names nothing. 410: expired. 429: too many requests. Anything else may pass. */
const linkStateOfError = (error: unknown): TerminalLinkState => {
  if (error instanceof ClientQuizError) {
    if (error.status === 410) return 'expired';
    if (error.status === 429) return 'busy';
    if (error.status === 400 || error.status === 404) return 'invalid';
  }
  return 'offline';
};

/**
 * Resolves a `?t=` link. A bare link is a guest preview.
 * `type` is the quiz this page plays: a link for the other quiz is never played here.
 */
export const usePublicQuizLink = (token: string | null, type: QuizType) => {
  const [link, setLink] = useState<LinkState>(() => (token ? 'loading' : 'guest'));
  const [quiz, setQuiz] = useState<PublicQuiz | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    getPublicQuiz(token)
      .then(result => {
        if (cancelled) return;
        if (result.type !== type) {
          setLink('mismatch');
        } else if (result.status === 'completed') {
          setLink('done');
        } else if (result.expired) {
          setLink('expired');
        } else {
          setQuiz(result);
          setLink('ready');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) setLink(linkStateOfError(error));
      });
    return () => {
      cancelled = true;
    };
  }, [token, attempt, type]);

  const reload = useCallback(() => {
    setLink('loading');
    setAttempt(value => value + 1);
  }, []);

  return { link, setLink, quiz, reload };
};

/** The one-line states a `?t=` link can resolve to other than a working quiz; `offline` and `busy` offer 重試. */
export const LinkMessage = (props: {
  state: TerminalLinkState;
  testIdPrefix: string;
  className: string;
  onRetry?: () => void;
}) => {
  const t = useTx();
  return (
    <div className={props.className} data-testid={`${props.testIdPrefix}-link-state`} data-state={props.state}>
      <p className="cq-question">{t(LINK_STATE_MESSAGES[props.state])}</p>
      {(props.state === 'offline' || props.state === 'busy') && props.onRetry && (
        <button
          type="button"
          className="cq-btn-secondary"
          data-testid={`${props.testIdPrefix}-link-retry`}
          onClick={props.onRetry}
        >
          {t(RETRY)}
        </button>
      )}
    </div>
  );
};

/** A submit failed for a reason worth trying again for (not 410/409). */
export const RetryBanner = (props: {
  onRetry: () => void;
  disabled: boolean;
  testIdPrefix: string;
  className: string;
}) => {
  const t = useTx();
  return (
    <div className={props.className} data-testid={`${props.testIdPrefix}-retry`}>
      <span>
        {t(
          L(
            '未能送出，請檢查網絡後再試',
            '未能送出，请检查网络后再试',
            "Couldn't send. Please check your connection and try again."
          )
        )}
      </span>
      <button type="button" className="cq-btn-secondary" disabled={props.disabled} onClick={props.onRetry}>
        {t(RETRY)}
      </button>
    </div>
  );
};

/** No reply (status 0) or a 5xx: the request may have been stored before it failed. */
const mayHaveLanded = (error: unknown) =>
  !(error instanceof ClientQuizError) || error.status === 0 || error.status >= 500;

/**
 * Sends answers once per tap. `sendingRef` guards a double tap landing in the
 * same tick, before `sending` (which disables the buttons) has re-rendered.
 * 410 → the link has since expired; 409 → already completed (or, with
 * `on409: 'proceed'`, carry on as if it had worked). Anything else leaves the
 * answers in place and sets `failed` to the name of what failed, so the page
 * can offer 重試 by calling its own handler again — with the answers as they
 * are now, not as they were at the failed tap.
 *
 * A 409 on a retry whose earlier try may have landed (lost reply, 5xx) is that
 * earlier try's own success, not someone else's: it carries on to the result
 * rather than telling the client the quiz is already done.
 */
export const useQuizSubmit = <Kind extends string>(setLink: (state: LinkState) => void) => {
  const [failed, setFailed] = useState<Kind | null>(null);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const mayHaveLandedRef = useRef<Partial<Record<Kind, boolean>>>({});

  const submit = (
    kind: Kind,
    action: () => Promise<void>,
    onSuccess: () => void,
    on409: 'done' | 'proceed' = 'done'
  ) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setFailed(null);
    action()
      .then(() => {
        mayHaveLandedRef.current[kind] = false;
        onSuccess();
      })
      .catch((error: unknown) => {
        if (error instanceof ClientQuizError && error.status === 410) {
          setLink('expired');
          return;
        }
        if (error instanceof ClientQuizError && error.status === 409) {
          if (on409 === 'proceed' || mayHaveLandedRef.current[kind]) onSuccess();
          else setLink('done');
          return;
        }
        if (mayHaveLanded(error)) mayHaveLandedRef.current[kind] = true;
        setFailed(kind);
      })
      .finally(() => {
        sendingRef.current = false;
        setSending(false);
      });
  };

  return { submit, sending, failed };
};
